<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  clearingAll: {
    type: Boolean,
    default: false,
  },
  conversations: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits([
  'close',
  'selectConversation',
  'removeConversation',
  'renameConversation',
  'renameConversationInitiated',
  'deleteConversationInitiated',
  'deleteConversationCanceled',
  'clearAll',
])

const localConversations = ref(props.conversations)
const showClearAllConfirm = ref(false)
const confirmDialogReference = ref(null)
const showDeleteConfirm = ref(false)
const deleteConfirmDialogReference = ref(null)
const deleteCandidateId = ref(null)
const renamingConversationId = ref(null)
const renamingConversation = ref(null)
const titleDraft = ref('')
const renameError = ref('')
const isSavingRename = ref(false)
const editingRowReference = ref(null)
const renameInputReference = ref(null)
let titleSelectClickTimeout = null

function resolveElementReference(referenceValue) {
  if (!referenceValue)
    return null
  return Array.isArray(referenceValue) ? referenceValue.find(Boolean) ?? null : referenceValue
}

function setEditingRowReference(element) {
  editingRowReference.value = element
}

function setRenameInputReference(element) {
  renameInputReference.value = element
}

watch(() => props.conversations, (value) => {
  localConversations.value = value
}, { immediate: true })

watch(() => props.open, (isOpen) => {
  if (!isOpen) {
    if (!isSavingRename.value) {
      cancelRename()
    }
    showClearAllConfirm.value = false
    showDeleteConfirm.value = false
    deleteCandidateId.value = null
    nextTick(() => confirmDialogReference.value?.close())
    nextTick(() => deleteConfirmDialogReference.value?.close())
  }
})

watch(showClearAllConfirm, (value) => {
  nextTick(() => {
    if (value) {
      confirmDialogReference.value?.showModal()
    }
    else {
      confirmDialogReference.value?.close()
    }
  })
})

watch(showDeleteConfirm, (value) => {
  nextTick(() => {
    if (value) {
      deleteConfirmDialogReference.value?.showModal()
    }
    else {
      deleteConfirmDialogReference.value?.close()
    }
  })
})

const resultsCount = computed(() => localConversations.value.length)

function handleClose() {
  showClearAllConfirm.value = false
  emit('close')
}

function openClearAllConfirm() {
  showClearAllConfirm.value = true
}

function cancelClearAllConfirm() {
  showClearAllConfirm.value = false
}

function confirmClearAll() {
  showClearAllConfirm.value = false
  emit('clearAll')
}

function openDeleteConfirm(id) {
  if (!id) {
    return
  }

  deleteCandidateId.value = id
  showDeleteConfirm.value = true
  emit('deleteConversationInitiated', { conversationId: id, source: 'search_chats' })
}

function cancelDeleteConfirm() {
  const conversationId = deleteCandidateId.value
  if (conversationId) {
    emit('deleteConversationCanceled', { conversationId, source: 'search_chats' })
  }
  showDeleteConfirm.value = false
  deleteCandidateId.value = null
}

function confirmDeleteConversation() {
  const conversationId = deleteCandidateId.value
  if (!conversationId) {
    cancelDeleteConfirm()
    return
  }

  showDeleteConfirm.value = false
  emit('removeConversation', { conversationId, source: 'search_chats' })
  deleteCandidateId.value = null
}

function handleSelect(conversation) {
  emit('selectConversation', conversation)
  emit('close')
}

function handleTitleClick(conversation) {
  if (titleSelectClickTimeout) {
    clearTimeout(titleSelectClickTimeout)
  }

  titleSelectClickTimeout = setTimeout(() => {
    titleSelectClickTimeout = null
    handleSelect(conversation)
  }, 200)
}

function handleTitleDblClick(conversation) {
  if (titleSelectClickTimeout) {
    clearTimeout(titleSelectClickTimeout)
    titleSelectClickTimeout = null
  }

  beginRename(conversation)
}

function formatDate(value) {
  if (!value)
    return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

function beginRename(conversation) {
  const conversationKey = conversation.conversation_id || conversation.id
  if (!conversationKey) {
    return
  }

  renamingConversationId.value = conversationKey
  renamingConversation.value = conversation
  titleDraft.value = conversation.title || ''
  renameError.value = ''
  emit('renameConversationInitiated', { conversationId: conversationKey, source: 'search_chats' })
}

function cancelRename() {
  renamingConversationId.value = null
  renamingConversation.value = null
  titleDraft.value = ''
  renameError.value = ''
}

async function saveRename(conversation) {
  if (isSavingRename.value) {
    return
  }

  const conversationKey = conversation.conversation_id || conversation.id
  const nextTitle = titleDraft.value.trim()

  if (!nextTitle) {
    renameError.value = 'empty'
    return
  }

  const currentTitle = (conversation.title || conversation.name || '').trim()
  if (nextTitle === currentTitle) {
    cancelRename()
    return
  }

  isSavingRename.value = true
  renameError.value = ''
  try {
    await new Promise((resolve, reject) => {
      emit('renameConversation', {
        conversationId: conversationKey,
        title: nextTitle,
        source: 'history',
        onSettled: (error) => {
          if (error) {
            reject(error)
          }
          else {
            resolve()
          }
        },
      })
    })
    cancelRename()
  }
  catch {
    renameError.value = 'request'
  }
  finally {
    isSavingRename.value = false
  }
}

function commitRename() {
  const conversation = renamingConversation.value
    ?? localConversations.value.find(c => (c.conversation_id || c.id) === renamingConversationId.value)
  if (conversation) {
    saveRename(conversation)
  }
}

function handleRenamePointerOutside(event) {
  if (!renamingConversationId.value || isSavingRename.value) {
    return
  }

  const editRoot = resolveElementReference(editingRowReference.value) ?? resolveElementReference(renameInputReference.value)
  if (editRoot?.contains(event.target)) {
    return
  }

  commitRename()
}

watch(renamingConversationId, (conversationId) => {
  if (conversationId) {
    nextTick(() => {
      const input = resolveElementReference(renameInputReference.value)
      input?.focus()
      input?.select()
      document.addEventListener('pointerdown', handleRenamePointerOutside, true)
    })
    return
  }

  document.removeEventListener('pointerdown', handleRenamePointerOutside, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleRenamePointerOutside, true)
  if (titleSelectClickTimeout) {
    clearTimeout(titleSelectClickTimeout)
    titleSelectClickTimeout = null
  }
})
</script>

<template>
  <teleport to="body">
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0"
      enter-to-class="transform opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="transform opacity-100"
      leave-to-class="transform opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4"
        @click.self="handleClose"
      >
        <div class="inset-0 z-[-1] bg-black opacity-40 w-full h-full absolute top-0 left-0" />
        <div class="flex w-full max-w-[590px] max-h-[480px] flex-col overflow-hidden rounded-2xl bg-white">
          <div class="flex items-center justify-between border-b border-dss-surface-200 px-6 py-4">
            <h2 class="font-inter text-xl font-bold leading-none tracking-[0.005em]">
              {{ $t('newBradley.searchModal.title') }}
            </h2>
            <button
              class="rounded-lg p-2 transition-colors hover:bg-gray-100"
              @click="handleClose"
            >
              <i class="pi pi-times text-lg text-gray-600" />
            </button>
          </div>

          <div class="border-b border-dss-surface-200 px-6 py-3">
            <p class="text-sm text-gray-600">
              {{ $t('newBradley.searchModal.showing') }} {{ resultsCount }} {{ $t('newBradley.searchModal.results') }}
            </p>
          </div>

          <div class="flex-1 overflow-y-auto overflow-x-hidden px-6 py-4">
            <div
              v-if="loading"
              class="flex flex-col items-center justify-center gap-3 py-12 text-gray-500"
            >
              <i class="pi pi-spinner animate-spin text-2xl" />
              <p class="text-sm">
                {{ $t('newBradley.searchModal.loadingConversations') }}
              </p>
            </div>

            <div
              v-else-if="!localConversations.length"
              class="flex flex-col items-center justify-center gap-3 py-12 text-gray-500"
            >
              <i class="pi pi-comments text-2xl" />
              <p class="text-sm font-semibold">
                {{ $t('newBradley.searchModal.noConversationsFound') }}
              </p>
              <p class="text-xs text-gray-400">
                {{ $t('newBradley.searchModal.tryRefining') }}
              </p>
            </div>

            <div
              v-else
              class="space-y-1"
            >
              <div
                v-for="conversation in localConversations"
                :key="conversation.conversation_id || conversation.id"
                class="rounded-lg px-3 py-2 transition hover:bg-gray-50"
                :class="renamingConversationId === (conversation.conversation_id || conversation.id) ? 'search-modal__row--editing' : 'flex min-w-0 items-start justify-between gap-3'"
              >
                <template v-if="renamingConversationId === (conversation.conversation_id || conversation.id)">
                  <div
                    :ref="setEditingRowReference"
                    class="search-modal__rename-row"
                  >
                    <input
                      :ref="setRenameInputReference"
                      v-model="titleDraft"
                      type="text"
                      maxlength="120"
                      class="search-modal__title-input"
                      :disabled="isSavingRename"
                      @keydown.enter.prevent="saveRename(conversation)"
                      @keydown.esc.prevent="cancelRename"
                    >
                    <button
                      type="button"
                      class="search-modal__save-btn"
                      :disabled="isSavingRename"
                      @click="saveRename(conversation)"
                    >
                      <i
                        v-if="isSavingRename"
                        class="pi pi-spinner animate-spin text-sm"
                      />
                      <i
                        v-else
                        class="pi pi-save text-sm"
                      />
                      <span>{{ $t('newBradley.searchModal.save') }}</span>
                    </button>
                  </div>
                  <p
                    v-if="renameError === 'empty'"
                    class="mt-1.5 text-xs text-red-600"
                  >
                    {{ $t('newBradley.searchModal.renameErrorEmpty') }}
                  </p>
                  <p
                    v-else-if="renameError === 'request'"
                    class="mt-1.5 text-xs text-red-600"
                  >
                    {{ $t('newBradley.searchModal.renameErrorRequest') }}
                  </p>
                </template>
                <template v-else>
                  <button
                    class="min-w-0 flex-1 whitespace-normal wrap-break-word text-left text-sm text-gray-700 hover:text-indigo-600"
                    @click="handleSelect(conversation)"
                  >
                    <span
                      @click.stop="handleTitleClick(conversation)"
                      @dblclick.stop="handleTitleDblClick(conversation)"
                    >{{ conversation.title || $t('newBradley.searchModal.conversation') }}</span>
                    <span
                      v-if="conversation.updated_at"
                      class="block text-xs text-gray-400"
                    >{{ $t('newBradley.searchModal.updated') }} {{ formatDate(conversation.updated_at) }}</span>
                  </button>
                  <div class="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      class="search-modal__rename-pill"
                      :disabled="isSavingRename"
                      @click.stop="beginRename(conversation)"
                    >
                      <i class="pi pi-pencil search-modal__rename-pill-icon" />
                      <span>{{ $t('newBradley.searchModal.rename') }}</span>
                    </button>
                    <button
                      type="button"
                      class="rounded p-1 text-gray-400 transition hover:bg-gray-200 hover:text-gray-600"
                      :disabled="isSavingRename"
                      @click="openDeleteConfirm(conversation.conversation_id || conversation.id)"
                    >
                      <i class="pi pi-trash text-sm" />
                    </button>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-end gap-2 border-t border-dss-surface-200 px-6 py-4">
            <button
              type="button"
              class="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition opacity-90 hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="loading || clearingAll || !localConversations.length"
              :style="{ backgroundColor: '#EF4444' }"
              @click="openClearAllConfirm"
            >
              <i
                v-if="clearingAll"
                class="pi pi-spinner animate-spin"
              />
              {{ clearingAll ? $t('newBradley.searchModal.clearAllLoading') : $t('newBradley.searchModal.clearAll') }}
            </button>
            <button
              class="rounded-lg bg-dss-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-dss-primary-600"
              @click="handleClose"
            >
              {{ $t('newBradley.searchModal.close') }}
            </button>
          </div>
        </div>

        <dialog
          ref="confirmDialogReference"
          class="clear-all-confirm-dialog m-auto max-w-md rounded-2xl border-0 bg-white p-6 shadow-xl"
          aria-labelledby="clear-all-confirm-title"
          @close="cancelClearAllConfirm"
        >
          <h3
            id="clear-all-confirm-title"
            class="text-lg font-semibold"
          >
            {{ $t('newBradley.searchModal.clearAllConfirmTitle') }}
          </h3>
          <p class="mt-2 text-sm text-gray-600">
            {{ $t('newBradley.searchModal.clearAllConfirmMessage') }}
          </p>
          <div class="mt-6 flex justify-end gap-2">
            <button
              type="button"
              class="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition opacity-90 hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="clearingAll"
              :style="{ backgroundColor: '#EF4444' }"
              @click="confirmClearAll"
            >
              <i
                v-if="clearingAll"
                class="pi pi-spinner animate-spin"
              />
              {{ clearingAll ? $t('newBradley.searchModal.clearAllLoading') : $t('newBradley.searchModal.clearAllConfirmButton') }}
            </button>
            <button
              type="button"
              class="rounded-lg bg-dss-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-dss-primary-600"
              @click="cancelClearAllConfirm"
            >
              {{ $t('newBradley.searchModal.clearAllConfirmCancel') }}
            </button>
          </div>
        </dialog>

        <dialog
          ref="deleteConfirmDialogReference"
          class="clear-all-confirm-dialog m-auto max-w-md rounded-2xl border-0 bg-white p-6 shadow-xl"
          aria-labelledby="delete-confirm-title"
          @close="cancelDeleteConfirm"
        >
          <h3
            id="delete-confirm-title"
            class="text-lg font-semibold"
          >
            {{ $t('newBradley.deleteModal.title') }}
          </h3>
          <p class="mt-2 text-sm text-gray-600">
            {{ $t('newBradley.searchModal.deleteConfirmMessage') }}
          </p>
          <div class="mt-6 flex justify-end gap-2">
            <button
              type="button"
              class="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition opacity-90 hover:opacity-100"
              :style="{ backgroundColor: '#EF4444' }"
              @click="confirmDeleteConversation"
            >
              {{ $t('newBradley.deleteModal.delete') }}
            </button>
            <button
              type="button"
              class="rounded-lg bg-indigo-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-800"
              @click="cancelDeleteConfirm"
            >
              {{ $t('newBradley.deleteModal.cancel') }}
            </button>
          </div>
        </dialog>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
.clear-all-confirm-dialog {
  position: fixed;
  inset: 0;
  margin: auto;
  width: 100%;
  max-width: 28rem;
}

.clear-all-confirm-dialog::backdrop {
  background: rgba(0, 0, 0, 0.5);
}

.search-modal__row--editing {
  display: block;
  width: 100%;
  min-width: 0;
}

.search-modal__rename-row {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 0.5rem;
}

.search-modal__title-input {
  flex: 1 1 0%;
  min-width: 0;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  background: #ffffff;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #475569;
}

.search-modal__title-input:focus {
  border-color: #94a3b8;
  outline: none;
  box-shadow: 0 0 0 2px rgba(37, 39, 79, 0.12);
}

.search-modal__title-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.search-modal__save-btn {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #ffffff;
  background: var(--p-primary-500);
  cursor: pointer;
  transition: background-color 0.15s ease, opacity 0.15s ease;
}

.search-modal__save-btn:hover:not(:disabled) {
  background: var(--p-primary-600);
}

.search-modal__save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.search-modal__rename-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  border: 1px solid #b0b2da;
  border-radius: 6px;
  background: #ffffff;
  padding: 0.35rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #25274f;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.search-modal__rename-pill:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #b0b2da;
}

.search-modal__rename-pill:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.search-modal__rename-pill-icon {
  font-size: 0.75rem;
  color: #25274f;
}
</style>
